import { renderHeader } from '../components/header.js';
import { renderNav } from '../components/nav.js';
import { loadGoals, loadRunPlan, loadRuns, loadSettings, saveRunSchedule } from '../lib/grit-data.js';
import { raceMphFromGoal, DEFAULT_EASY_MPH, planWeekNumber } from '../lib/run-plan.js';
import { scheduleWeek, defaultSchedule, cleanSchedule, raceProposal, logTypeFor, changeTarget } from '../lib/run-schedule.js';
import { renderRunWeek, bindRunWeek } from '../components/run-week.js';
import { openRunPlanEditor, openChangeNextRun } from '../components/run-plan-editor.js';
import { applyRunSideEffects } from '../lib/run-progress.js';
import { paceZones } from '../lib/running-paces.js';
import { projectGoal } from '../lib/mile-projection.js';
import { openRunLog } from '../components/run-log-sheet.js';
import { icon } from '../components/icons.js';
import { toast } from '../components/toast.js';
import { viewing, label, isViewingSelf } from '../lib/auth.js';

// Running, plan v4 (2026-10-15): each person's own saved plan is the one source.
// Runs start at 1 mile, targets are miles or minutes, nothing goes up on its own,
// and a race date only proposes a change.

const fmtMile = (sec) => `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}`;
const today = () => new Date();
function daysUntil(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return Math.ceil((new Date(y, m - 1, d) - today()) / 86400000);
}

function runMode(user = viewing()) {
  return localStorage.getItem('grit-runmode-' + user) || 'treadmill';
}
function setRunMode(user, mode) { localStorage.setItem('grit-runmode-' + user, mode); }

function mphPace(mph) {
  const sec = Math.round((60 / Number(mph)) * 60);
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

function modeToggle(mode) {
  return `<div class="grit-seg mb-4 max-w-[240px]">
    ${[['treadmill', 'Treadmill'], ['outside', 'Outside']].map(([m, lbl]) =>
      `<button data-mode="${m}" class="${mode === m ? 'on' : ''}">${lbl}</button>`).join('')}
  </div>`;
}

// The saved plan, or the 1-mile starting plan if this person has never saved one.
export function scheduleFor(planRow, user) {
  const saved = cleanSchedule(planRow?.plan);
  return saved.length || Array.isArray(planRow?.plan) ? saved : defaultSchedule(user);
}

export function renderRunning(el) {
  el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-4"><p class="text-inksoft">Loading…</p></div>${renderNav('progress')}`;
  (async () => {
    const [goals, plan, runs, settings] = await Promise.all([loadGoals(), loadRunPlan(), loadRuns(), loadSettings()]);
    paint(el, { goals, plan, runs, settings });
  })();
}

function paint(el, { goals, plan, runs, settings }) {
  const user = viewing();
  const self = isViewingSelf();
  const mode = runMode();
  const schedule = scheduleFor(plan, user);
  const week = scheduleWeek(schedule);

  const top = mileCard(goals, runs, mode);
  const proposal = self ? raceProposal(plan?.race_date, schedule) : null;
  const race = goals.find((g) => g.kind === 'race_10k');
  const raceCard = proposal ? `<div class="grit-card p-4 mt-3">
      <div class="flex items-center justify-between"><div class="text-[17px] font-semibold text-ink">Race coming up</div>${race?.target_date ? `<span class="grit-chip-accent">${Math.max(0, daysUntil(race.target_date))} days</span>` : ''}</div>
      <div class="text-[15px] text-inksoft mt-1">${proposal.runId ? `For this week, the build-up suggests your longest run at <b class="text-ink">${proposal.suggestedMi} mi</b> (now ${proposal.currentMi} mi). Your plan only changes if you say so.` : `The build-up suggests a <b class="text-ink">${proposal.suggestedMi} mi</b> run this week. Add one in Edit plan if you want it.`}</div>
      ${proposal.runId ? '<button id="use-proposal" class="grit-btn-quiet mt-3 !text-grit">Use the suggestion</button>' : ''}
      ${race?.target ? `<div class="text-[13px] text-inksoft mt-2">Race pace for sub-${race.target}: ${mode === 'outside' ? mphPace(raceMphFromGoal(race.target)) + '/mi' : raceMphFromGoal(race.target) + ' mph'}</div>` : ''}
    </div>` : '';

  const easyMph = Number(settings.run_easy_mph) || DEFAULT_EASY_MPH;
  const foot = `Easy pace now <b class="text-ink">${mode === 'outside' ? mphPace(easyMph) + '/mi' : easyMph + ' mph'}</b>, adjusts from how your easy runs feel.`;

  el.innerHTML = `
    ${renderHeader()}
    <div class="grit-page px-5 pt-4">
      <div class="grit-eyebrow">${label(user)}</div>
      <h1 class="grit-title mt-0.5 mb-4">Running</h1>
      ${modeToggle(mode)}
      ${top}
      ${raceCard}
      <div class="mt-3">${renderRunWeek({ week, runs, canLog: self, footNote: foot })}</div>
      ${recentRunsCard(runs, self)}
      ${self ? '<button id="logrun" class="grit-btn mt-4">Log a run</button>' : ''}
    </div>
    ${renderNav('progress')}`;

  const rerender = () => renderRunning(el);
  el.querySelectorAll('[data-mode]').forEach((b) => b.onclick = () => { setRunMode(user, b.dataset.mode); rerender(); });

  const afterSave = async (run) => {
    const fx = await applyRunSideEffects(run);
    if (fx?.easyMph) toast(fx.easyMph > fx.prevEasyMph ? `Easy pace up to ${fx.easyMph} mph` : `Eased your pace to ${fx.easyMph} mph`);
    rerender();
  };

  if (self) {
    bindRunWeek(el, {
      week,
      onLog: (r) => openRunLog({
        defaultType: logTypeFor(r), mode,
        planContext: { week: planWeekNumber(plan?.race_date), slot: r.slot, plannedDistanceMi: r.distanceMi, plannedMinutes: r.minutes, type: logTypeFor(r) },
        onSaved: afterSave,
      }),
      onChange: (r) => openChangeNextRun({ schedule, runId: r.slot, onSaved: rerender }),
    });
    const ep = document.getElementById('edit-plan');
    if (ep) ep.onclick = () => openRunPlanEditor({ schedule, onSaved: rerender });
    const lr = document.getElementById('logrun');
    if (lr) lr.onclick = () => openRunLog({ defaultType: 'easy', mode, onSaved: afterSave });
    el.querySelectorAll('[data-editrun]').forEach((b) => b.onclick = () => {
      const run = runs.find((x) => x.id === b.dataset.editrun);
      if (run) openRunLog({ existing: run, mode, onSaved: rerender });
    });
    const up = document.getElementById('use-proposal');
    if (up) up.onclick = async () => {
      const updated = schedule.map((r) => (r.id === proposal.runId ? changeTarget(r, proposal.suggestedMi) : r));
      const ok = await saveRunSchedule(updated);
      toast(ok ? 'Plan updated' : "Couldn't save, try again", { tone: ok ? 'good' : 'bad' });
      if (ok) rerender();
    };
  }
}

// ── Mile goal card (both users, 2026-10-22) ───────────────────────────────────
function mileCard(goals, runs, mode) {
  const mileGoal = goals.find((g) => g.kind === 'mile_time');
  const cur = Number(mileGoal?.current || 650);
  const goal = Number(mileGoal?.target || 450);
  const z = paceZones(cur);
  const milestone = mode === 'outside' ? `${z.mile.pace}/mi` : `${z.mile.mph} mph`;
  const trials = runs.filter((r) => r.type === 'timetrial' && r.avg_pace);
  let projLine = 'Log a mile test to start your forecast. It updates from your real times.';
  if (trials.length >= 2) {
    const base = new Date(trials[trials.length - 1].date);
    const pts = trials.map((r) => ({ daysFromStart: Math.round((new Date(r.date) - base) / 86400000), mileSec: paceSec(r.avg_pace) })).reverse();
    const p = projectGoal(pts, goal);
    projLine = p?.improving ? `On your trend: ${fmtMile(goal)} in about ${Math.round(p.daysToGoal / 7)} weeks.` : 'Keep logging mile tests, the trend will sharpen.';
  }
  const frac = Math.max(0, Math.min(1, (650 - cur) / (650 - goal)));
  return `<div class="grit-card p-5">
    <div class="flex items-end justify-between">
      <div><div class="grit-eyebrow">Current mile</div><div class="font-rounded font-bold text-ink leading-none mt-1 tabular-nums" style="font-size:30px">${trials.length ? fmtMile(cur) : '<span class="text-[20px]">Not tested yet</span>'}</div></div>
      <div class="text-right"><div class="grit-eyebrow">Goal</div><div class="font-rounded text-[22px] font-bold text-grit leading-none mt-1 tabular-nums">${fmtMile(goal)}</div></div>
    </div>
    <div class="h-2 rounded-full bg-fill mt-5 overflow-hidden"><div class="h-full rounded-full bg-brand" style="width:${Math.round(frac * 100)}%"></div></div>
    <div class="text-[15px] text-inksoft mt-3">${projLine}${trials.length ? ` Mile pace now: <b class="text-ink">${milestone}</b>.` : ''}</div>
  </div>`;
}

function paceSec(mmss) { const [m, s] = String(mmss).split(':').map(Number); return (m || 0) * 60 + (s || 0); }

function runDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

const TYPE_NAME = { timetrial: 'Mile test', lowimpact: 'Easy cardio', runwalk: 'Run/walk', easy: 'Easy run', long: 'Longer run', intervals: 'Intervals', tempo: 'Tempo run' };

function recentRunsCard(runs, canEdit) {
  const recent = runs.filter((r) => r.type && r.type !== 'hiit').slice(0, 6);
  if (!recent.length) return '';
  return `<div class="grit-card p-0 mt-3 overflow-hidden">
    <div class="text-[17px] font-semibold text-ink px-4 pt-3 pb-1">Recent runs</div>
    ${recent.map((r) => {
      const what = [r.actual_distance_mi ? `${r.actual_distance_mi} mi` : '', r.actual_minutes ? `${Math.round(r.actual_minutes)} min` : '', r.avg_pace ? `${r.avg_pace}/mi` : ''].filter(Boolean).join(' · ');
      const inner = `<div class="min-w-0"><div class="font-semibold text-[15px] text-ink">${TYPE_NAME[r.type] || 'Run'}</div><div class="text-[13px] text-inksoft">${runDate(r.date)}${r.rpe ? ` · effort ${r.rpe}/10` : ''}${r.knee_feel && r.knee_feel !== 'good' ? ` · knee: ${r.knee_feel}` : ''}</div></div>
        <div class="flex items-center gap-1 flex-none"><span class="text-[15px] font-semibold text-ink tabular-nums">${what}</span>${canEdit ? `<span class="text-inksoft">${icon.right('w-4 h-4')}</span>` : ''}</div>`;
      return canEdit
        ? `<button data-editrun="${r.id}" class="grit-row w-full flex items-center justify-between gap-3 px-4 min-h-[56px] py-2 text-left border-t border-hairline">${inner}</button>`
        : `<div class="flex items-center justify-between gap-3 px-4 min-h-[56px] py-2 border-t border-hairline">${inner}</div>`;
    }).join('')}
  </div>`;
}
